/**
 * Form Progress Service
 * Saves and loads form filling progress
 */

interface FormProgress {
  stepId: string;
  formCode: string;
  formData: Record<string, any>;
  lastSaved: string;
  currentSection?: number;
}

class FormProgressService {
  private readonly STORAGE_PREFIX = 'form_progress_';

  /**
   * Save form progress to localStorage
   */
  saveProgress(stepId: string, formCode: string, formData: Record<string, any>, currentSection?: number): void {
    try {
      const progress: FormProgress = {
        stepId,
        formCode,
        formData,
        lastSaved: new Date().toISOString(),
        currentSection,
      };

      const key = `${this.STORAGE_PREFIX}${stepId}_${formCode}`;
      localStorage.setItem(key, JSON.stringify(progress));
    } catch (error) {
      console.error('Error saving form progress:', error);
    }
  }

  /**
   * Load form progress from localStorage
   */
  loadProgress(stepId: string, formCode: string): FormProgress | null {
    try {
      const key = `${this.STORAGE_PREFIX}${stepId}_${formCode}`;
      const data = localStorage.getItem(key);
      
      if (data) {
        return JSON.parse(data);
      }
    } catch (error) {
      console.error('Error loading form progress:', error);
    }
    return null;
  }

  /**
   * Clear form progress
   */
  clearProgress(stepId: string, formCode: string): void {
    try {
      const key = `${this.STORAGE_PREFIX}${stepId}_${formCode}`;
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error clearing form progress:', error);
    }
  }

  /**
   * Get all saved progress for a step
   */
  getAllProgressForStep(stepId: string): FormProgress[] {
    const progress: FormProgress[] = [];
    
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(`${this.STORAGE_PREFIX}${stepId}_`)) {
          const data = localStorage.getItem(key);
          if (data) {
            progress.push(JSON.parse(data));
          }
        }
      }
    } catch (error) {
      console.error('Error getting all progress:', error);
    }
    
    return progress;
  }

  /**
   * Check if progress exists for a step
   */
  hasProgress(stepId: string, formCode: string): boolean {
    const key = `${this.STORAGE_PREFIX}${stepId}_${formCode}`;
    return localStorage.getItem(key) !== null;
  }
}

export const formProgressService = new FormProgressService();

